import { useNavigate } from 'react-router-dom';
import { StatutStockAppareil } from '@genviz/shared/enums';
import type { ResumeAlerte } from '@genviz/shared/dto';

import { useTableauDeBord } from '../api/queries';
import type { AppareilMuet } from '../api/types';
import { BadgeCriticite, BadgeEtatAppareil, PastilleNombre } from '../components/Badges';
import { Bouton } from '../components/Boutons';
import { Carte, Tuile } from '../components/Carte';
import { Chargement } from '../components/Chargement';
import { MessageErreur } from '../components/MessageErreur';
import { Repartition, RepartitionParEtat, Sparkline } from '../components/Graphes';
import { Tableau, type Colonne } from '../components/Tableau';
import { LIBELLE_STATUT_STOCK, LIBELLE_TYPE_ALERTE, VALEUR_ABSENTE } from '../i18n/fr';
import {
  formaterDateHeure,
  formaterDecimal,
  formaterDuree,
  formaterNombre,
  formaterRelatif,
  versDate,
} from '../lib/format';

/**
 * Tableau de bord — le premier écran qu'on regarde quand ça va mal.
 *
 * Il répond à quatre questions dans cet ordre, et à rien d'autre :
 *   1. La chaîne d'ingestion tourne-t-elle ? (sans elle, tous les autres chiffres mentent)
 *   2. Combien d'appareils sont muets ?
 *   3. Combien d'alertes critiques sont ouvertes, et depuis quand ?
 *   4. Où faut-il aller cliquer ?
 *
 * Les listes d'appareils muets et d'alertes critiques sont affichées ici plutôt que renvoyées à
 * un second écran : un opérateur en train de traiter un incident ne doit pas avoir à naviguer
 * pour savoir QUELS appareils sont concernés.
 */
export function TableauDeBordPage(): JSX.Element {
  const naviguer = useNavigate();
  const { data, error, isPending, isFetching, refetch } = useTableauDeBord();

  if (isPending) return <Chargement libelle="Chargement du tableau de bord…" />;
  if (error) {
    return (
      <MessageErreur
        erreur={error}
        action={
          <Bouton
            petit
            onClick={() => {
              void refetch();
            }}
          >
            Réessayer
          </Bouton>
        }
      />
    );
  }

  // Garde explicite : elle rend l'affinement de type indépendant de la forme exacte de l'union
  // renvoyée par TanStack Query, et couvre le cas d'un cache vidé pendant le rendu.
  if (!data) return <Chargement libelle="Chargement du tableau de bord…" />;

  const horloge = versDate(data.horloge_serveur) ?? new Date();
  const ingestion = data.ingestion;

  // Une ingestion silencieuse depuis plus de 10 minutes est une panne, pas un creux d'activité :
  // même au repos, un parc de plusieurs dizaines d'objets produit des heartbeats.
  const dernierMessage = versDate(ingestion.dernier_message_at);
  const silenceS = dernierMessage ? (horloge.getTime() - dernierMessage.getTime()) / 1000 : null;
  const ingestionEnPanne =
    !ingestion.broker_connecte || silenceS === null || silenceS > 600;

  type AlerteCritiqueTableau = ResumeAlerte & {
    numero_serie?: string;
    exploitation?: { id: string; nom: string };
  };

  const colonnesMuets: Array<Colonne<AppareilMuet>> = [
    {
      cle: 'sn',
      entete: 'N° de série',
      rendu: (a) => <span className="gv-mono">{a.numero_serie_electrificateur}</span>,
    },
    { cle: 'nom', entete: 'Nom', rendu: (a) => a.nom ?? VALEUR_ABSENTE },
    {
      cle: 'exploitation',
      entete: 'Exploitation',
      rendu: (a) => a.exploitation?.nom ?? VALEUR_ABSENTE,
    },
    { cle: 'etat', entete: 'État', rendu: (a) => <BadgeEtatAppareil etat={a.statut_metier} /> },
    {
      cle: 'silence',
      entete: 'Dernier message',
      rendu: (a) => (
        <span title={formaterDateHeure(a.dernier_message_at)}>
          {a.dernier_message_at ? formaterRelatif(a.dernier_message_at, horloge) : 'jamais vu'}
        </span>
      ),
    },
  ];

  const colonnesAlertes: Array<Colonne<AlerteCritiqueTableau>> = [
    { cle: 'numero', entete: 'N°', rendu: (a) => a.numero, numerique: true },
    { cle: 'type', entete: 'Type', rendu: (a) => LIBELLE_TYPE_ALERTE[a.type] },
    { cle: 'criticite', entete: 'Criticité', rendu: (a) => <BadgeCriticite criticite={a.criticite} /> },
    // BA_00009 — l'exploitation d'abord : c'est elle que l'industriel connaît.
    {
      cle: 'exploitation',
      entete: 'Exploitation',
      rendu: (a) => a.exploitation?.nom ?? VALEUR_ABSENTE,
    },
    {
      cle: 'appareil',
      entete: 'Appareil',
      rendu: (a) => (
        <span>
          {a.electrificateur.nom ?? VALEUR_ABSENTE}
          {a.numero_serie ? <span className="gv-mono gv-secondaire"> · {a.numero_serie}</span> : null}
        </span>
      ),
    },
    {
      cle: 'depuis',
      entete: 'Ouverte depuis',
      rendu: (a) => (
        <span title={formaterDateHeure(a.declenchee_at)}>
          {formaterRelatif(a.declenchee_at, horloge)}
        </span>
      ),
    },
  ];

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1">Tableau de bord</h1>
          <span className="gv-corps-s gv-secondaire">
            Données arrêtées au {formaterDateHeure(data.horloge_serveur)}
            {isFetching ? ' · actualisation…' : ''}
          </span>
        </div>
        <div className="gv-entete__actions">
          <Bouton
            onClick={() => {
              void refetch();
            }}
            occupe={isFetching}
          >
            Actualiser
          </Bouton>
        </div>
      </header>

      {ingestionEnPanne ? (
        <div className="gv-bandeau gv-bandeau--erreur" role="alert">
          <div className="gv-pile gv-pile--serree">
            <strong>La chaîne d’ingestion ne reçoit plus rien.</strong>
            <span>
              {ingestion.broker_connecte
                ? `Dernière trame reçue ${
                    ingestion.dernier_message_at
                      ? formaterRelatif(ingestion.dernier_message_at, horloge)
                      : '— jamais'
                  }. Les états affichés ci-dessous sont figés : ils ne reflètent plus le terrain.`
                : 'Le consommateur MQTT n’est pas connecté au broker. Vérifiez EMQX et le service d’ingestion.'}
            </span>
          </div>
        </div>
      ) : null}

      <div className="gv-grille gv-grille--4">
        <Tuile
          libelle="Parc appairé"
          valeur={formaterNombre(data.parc.total_appaires)}
          note={`${formaterNombre(data.parc.par_statut_stock[StatutStockAppareil.EN_STOCK])} en stock`}
        />
        <Tuile
          libelle="Appareils hors ligne"
          valeur={formaterNombre(data.parc.hors_ligne)}
          ton={data.parc.hors_ligne > 0 ? 'rouge' : 'vert'}
          note={
            data.parc.jamais_vus > 0
              ? `dont ${formaterNombre(data.parc.jamais_vus)} jamais vus depuis l’appairage`
              : 'tous les appareils appairés ont déjà transmis'
          }
        />
        <Tuile
          libelle="Alertes critiques ouvertes"
          valeur={formaterNombre(data.alertes.critiques_actives)}
          ton={data.alertes.critiques_actives > 0 ? 'rouge' : 'vert'}
          note={`${formaterNombre(data.alertes.critiques_agees_24h)} ouvertes depuis plus de 24 h`}
        />
        <Tuile
          libelle="Ingestion MQTT"
          valeur={formaterDecimal(ingestion.messages_par_minute)}
          unite="msg/min"
          ton={ingestionEnPanne ? 'rouge' : 'vert'}
          note={
            ingestion.dernier_message_at
              ? `dernière trame ${formaterRelatif(ingestion.dernier_message_at, horloge)}`
              : 'aucune trame reçue'
          }
        />
      </div>

      <div className="gv-grille gv-grille--2">
        <Carte
          titre="Trames reçues"
          sousTitre={`60 dernières minutes, pas de ${ingestion.pas_min} min`}
          actions={
            <span className={`gv-badge ${ingestion.broker_connecte ? 'gv-badge--vert' : 'gv-badge--rouge'}`}>
              <span className="gv-badge__point" aria-hidden="true" />
              {ingestion.broker_connecte ? 'Broker connecté' : 'Broker injoignable'}
            </span>
          }
        >
          <Sparkline
            points={ingestion.serie.map((p) => ({ t: p.t, valeur: p.messages }))}
            titreAccessible="Trames reçues par minute"
            unite="trames"
            hauteur={92}
          />
          <div className="gv-grille gv-grille--4" style={{ marginTop: 'var(--gv-esp-4)' }}>
            <div>
              <p className="gv-label" title="Modules ayant une session MQTT ouverte avec le broker à cet instant.">Objets connectés</p>
              <p className="gv-nb">{formaterNombre(ingestion.objets_connectes)}</p>
            </div>
            <div>
              <p className="gv-label">Rejets (1 h)</p>
              <p className="gv-nb" style={{ color: ingestion.rejets_1h > 0 ? 'var(--gv-orange)' : undefined }}>
                {formaterNombre(ingestion.rejets_1h)}
              </p>
            </div>
            <div>
              <p className="gv-label" title="Délai entre la réception d’une trame par le broker et son enregistrement en base. Au-delà de quelques secondes, l’ingestion prend du retard.">Retard de traitement</p>
              <p className="gv-nb">{formaterDuree(ingestion.retard_traitement_s)}</p>
            </div>
          </div>
        </Carte>

        <Carte titre="État du parc" sousTitre="Appareils appairés, par état métier">
          {/* BA_00007 — le nombre d'objets FONCTIONNELS : en ligne, ni critiques ni éteints. */}
          <p className="gv-corps" style={{ marginBottom: 'var(--gv-esp-3)' }}>
            <b>{formaterNombre((data.parc as { fonctionnels?: number }).fonctionnels ?? 0)}</b> objet(s)
            fonctionnel(s) sur {formaterNombre(data.parc.total_appaires)}
            <span className="gv-secondaire"> — en ligne, ni en alerte critique ni éteints</span>
          </p>
          <RepartitionParEtat parEtat={data.parc.par_etat} />
          <hr
            style={{
              border: 0,
              borderTop: '1px solid var(--gv-bordure)',
              margin: 'var(--gv-esp-4) 0',
            }}
          />
          <p className="gv-label" style={{ marginBottom: 'var(--gv-esp-2)' }}>
            Cycle de vie du stock
          </p>
          <Repartition
            parts={(Object.keys(data.parc.par_statut_stock) as StatutStockAppareil[]).map((s) => ({
              cle: s,
              libelle: LIBELLE_STATUT_STOCK[s],
              valeur: data.parc.par_statut_stock[s],
              couleur: 'var(--gv-texte-3)',
            }))}
          />
        </Carte>
      </div>

      <Carte
        titre="Appareils muets"
        sousTitre="Appairés mais sans trame récente — les dix plus anciens"
        actions={<PastilleNombre valeur={data.parc.hors_ligne} />}
        sansPadding
      >
        <Tableau
          colonnes={colonnesMuets}
          lignes={data.appareils_muets}
          cleDe={(a) => a.numero_serie_electrificateur}
          titreVide="Aucun appareil muet"
          descriptionVide="Tous les appareils appairés ont publié récemment."
          surClic={(a) => {
            naviguer(`/appareils/${encodeURIComponent(a.numero_serie_electrificateur)}`);
          }}
        />
      </Carte>

      <Carte
        titre="Dernières alertes critiques"
        sousTitre="Toutes exploitations confondues"
        actions={<PastilleNombre valeur={data.alertes.critiques_actives} />}
        sansPadding
      >
        <Tableau
          colonnes={colonnesAlertes}
          lignes={data.dernieres_alertes_critiques as AlerteCritiqueTableau[]}
          cleDe={(a) => a.id}
          titreVide="Aucune alerte critique ouverte"
          descriptionVide="Le parc est nominal sur l’ensemble des exploitations."
          surClic={(a) => {
            if (a.numero_serie) naviguer(`/appareils/${encodeURIComponent(a.numero_serie)}`);
          }}
        />
      </Carte>
    </>
  );
}
